import React, { useState, useCallback, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import { useDropzone } from "react-dropzone";
import { UnoStoreHooks } from "@simulanisindia/ui";
import axios from "axios";
import { SSO_URL } from "../../env";
import { AddImageIcon, CancelIcon, CloseIcon } from "../../assets/Icons";
import {
  CREATE_NEW_APPLICATION,
  UPDATE_APPLICATION,
  UPDATE_CHANNEL_CONFIGURATION,
  UPLOAD_CHANNEL_CONFIGURATION_LOGO,
} from "../../apiClient/apiEndpoints";
import apiClient from "../../apiClient";

const { useUnoSelector } = UnoStoreHooks;

const defaultFormValues = {
  name: "",
  logo: "",
  banner: "",
};

const EditChannel = ({ setEditChannelModal, channelData, editChannelModal, refresh }) => {
  const userData = useUnoSelector((state) => state.user);
  const [formValues, setFormValues] = useState(defaultFormValues);
  const [loading, setLoading] = useState(false);

  const onDropImages = useCallback(async (acceptedFiles, imageType) => {
    const file = acceptedFiles[0];
    const reader = new FileReader();

    reader.onload = () => {
      setFormValues((prev) => ({ ...prev, [imageType]: reader.result }));
    };

    reader.readAsDataURL(file);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("randomize", true);

    const uploadedImageData = await handleImageUpload(formData, imageType);
    setFormValues((prev) => ({ ...prev, [imageType]: uploadedImageData?.url }));
  }, []);

  const { getRootProps: getLogoRootProps, getInputProps: getLogoInputProps } = useDropzone({
    onDrop: (acceptedFiles) => onDropImages(acceptedFiles, "logo"),
    accept: "image/*",
    multiple: false,
  });
  const { getRootProps: getBannerRootProps, getInputProps: getBannerInputProps } = useDropzone({
    onDrop: (acceptedFiles) => onDropImages(acceptedFiles, "banner"),
    accept: "image/*",
    multiple: false,
  });

  const handleImageUpload = async (payload, imageType) => {
    try {
      const res = await axios.post(`${SSO_URL}${UPLOAD_CHANNEL_CONFIGURATION_LOGO}/${imageType}`, payload, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${userData.token}`,
        },
      });
      return res.data;
    } catch (error) {
      console.log(error);
    }
  };

  const handleCancelUpload = (event, field) => {
    event.stopPropagation();
    setFormValues((prev) => ({ ...prev, [field]: "" }));
  };

  const handleCancelClick = () => {
    setFormValues(defaultFormValues);
    setEditChannelModal(false);
  };

  const handleSave = async () => {
    if (loading) return;
    try {
      setLoading(true);
      const payload = {
        name: formValues?.name?.trim() || "",
        logo: formValues?.logo || "",
        banner: formValues?.banner || "",
        channelId: channelData?.channel?.uuid,
        configurationId: channelData?.configuration?.id,
      };
      await apiClient.post(`${SSO_URL}${UPDATE_CHANNEL_CONFIGURATION}`, payload);
      refresh();
      handleCancelClick();
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setFormValues({
      name: channelData?.channel?.name || "",
      logo: channelData?.configuration?.logo || "",
      banner: channelData?.configuration?.banner || "",
    });
  }, [channelData]);

  return (
    <Modal show={editChannelModal} centered onHide={handleCancelClick}>
      <Modal.Header className="modalheader">
        Edit Channel
        <span onClick={handleCancelClick}>
          <CloseIcon />
        </span>
      </Modal.Header>
      <Modal.Body className="pb-0">
        <div className="row">
          <div className="col-12 standardinput mt-3">
            <label>
              Channel Name <span>*</span>
            </label>
            <input
              autoComplete="off"
              name="name"
              id="name"
              className="input form-control"
              type="text"
              value={formValues.name}
              onChange={(e) => setFormValues((prev) => ({ ...prev, name: e.target.value }))}
            />
          </div>
          <div className="image-formbox py-4">
            <div {...getLogoRootProps()} className="dropbox">
              <input {...getLogoInputProps()} />
              {formValues?.logo ? (
                <div className="uploaded-container">
                  <img src={formValues.logo} alt="Uploaded" className="uploaded-image" />
                  <button onClick={(e) => handleCancelUpload(e, "logo")} className="cancel-button">
                    <CancelIcon />
                  </button>
                </div>
              ) : (
                <div className="placeholder">
                  <AddImageIcon />
                </div>
              )}
            </div>
            <div className="d-flex align-items-start flex-column justify-content-start">
              <p>Channel Logo</p>
              <p className="little-info">Recommended Format* PNG</p>
            </div>
          </div>
          <div className="image-formbox mt-0">
            <div {...getBannerRootProps()} className="dropbox">
              <input {...getBannerInputProps()} />
              {formValues?.banner ? (
                <div className="uploaded-container">
                  <img src={formValues.banner} alt="Uploaded" className="uploaded-image" />
                  <button onClick={(e) => handleCancelUpload(e, "banner")} className="cancel-button">
                    <CancelIcon />
                  </button>
                </div>
              ) : (
                <div className="placeholder">
                  <AddImageIcon />
                </div>
              )}
            </div>
            <div className="d-flex align-items-start flex-column justify-content-start">
              <p>Channel Banner</p>
              <p className="little-info">Recommended Format* PNG</p>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="footerbtn">
        <button className="cancel" onClick={handleCancelClick}>
          Cancel
        </button>
        {!loading ? (
          <button className="save" onClick={handleSave}>
            Save
          </button>
        ) : (
          <button className="loading save">
            <svg viewBox="25 25 50 50">
              <circle r="20" cy="50" cx="50"></circle>
            </svg>
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default EditChannel;
